import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Printer, ArrowLeft } from "lucide-react";
import { adminList } from "@/lib/api";
import { TIME_SLOTS, humanSlot, paletteSummary, addonsSummary } from "@/data/collections";
import { MapLink } from "@/components/AdminDetails";

const TOKEN_KEY = "pp_admin_token";

export default function PrintDay() {
  const [sp] = useSearchParams();
  const date = sp.get("date") || "";
  const [bookings, setBookings] = useState(null);
  const [error, setError] = useState("");
  const token = localStorage.getItem(TOKEN_KEY) || "";

  useEffect(() => {
    if (!token) { setError("Please sign in to the dashboard first."); return; }
    adminList(token).then((r) => setBookings(r.bookings || [])).catch(() => setError("Could not load bookings."));
  }, [token]);

  const items = useMemo(() => (bookings || []).filter((b) => b.date === date && b.status !== "denied"), [bookings, date]);
  const d = date ? new Date(date + "T12:00:00") : null;

  return (
    <div className="min-h-screen bg-white text-black">
      <style>{`@media print { .no-print, a[data-testid^="print-map-"] { display: none !important; } body { background: #fff; } @page { margin: 14mm; } }`}</style>
      <div className="no-print sticky top-0 flex items-center justify-between gap-4 px-6 py-3 border-b" style={{ backgroundColor: "var(--pp-cream)", borderColor: "var(--pp-border)" }}>
        <Link to="/admin" data-testid="print-back" className="pp-btn-ghost" style={{ color: "var(--pp-text-muted)" }}><ArrowLeft className="h-4 w-4" /> Dashboard</Link>
        <button onClick={() => window.print()} data-testid="print-button" className="pp-btn !rounded-full !py-2 !px-5 !text-[0.7rem]"><Printer className="h-4 w-4" /> Print / Save PDF</button>
      </div>

      <main className="max-w-3xl mx-auto px-6 py-10" data-testid="print-sheet">
        <header className="flex items-end justify-between border-b-2 border-black pb-3 mb-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Pumpkin &amp; Porch · Install day sheet</div>
            <h1 className="font-serif-display text-4xl leading-tight" data-testid="print-title">
              {d ? d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" }) : "No date selected"}
            </h1>
          </div>
          <div className="text-right text-xs text-neutral-600">
            {items.length} install{items.length === 1 ? "" : "s"}<br />pumpkinandporchhelp@gmail.com
          </div>
        </header>

        {error && <p className="text-sm text-red-700">{error}</p>}
        {bookings && items.length === 0 && !error && <p className="text-sm text-neutral-600" data-testid="print-empty">No installs scheduled for this day.</p>}

        <ol className="space-y-6">
          {TIME_SLOTS.flatMap((slot) => items.filter((b) => b.time_slot === slot)).map((b, i) => (
            <li key={b.id} className="break-inside-avoid border border-neutral-300 p-4" data-testid={`print-item-${b.reference}`}>
              <div className="flex items-baseline justify-between gap-4 border-b border-neutral-200 pb-2 mb-3">
                <div className="font-serif-display text-2xl">{i + 1}. {humanSlot(b.time_slot)} — {b.full_name}{b.is_gift ? ` (gift for ${b.recipient_name})` : ""}</div>
                <div className="text-xs uppercase tracking-widest text-neutral-500" data-testid={`print-badges-${b.reference}`}>{b.reference} · {b.status} · {b.deposit_paid ? "Paid" : "Unpaid"}{b.completed ? " · Done" : ""}</div>
              </div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
                <Field k="Address" v={<MapLink address={b.address} testId={`print-map-${b.reference}`} />} wide />
                <Field k="Phone" v={b.phone} />
                <Field k="Email" v={b.email} />
                <Field k="Collection" v={`${b.collection_name} · ${b.size_label}`} />
                <Field k="Add-ons" v={addonsSummary(b)} wide />
                {paletteSummary(b.palette) && <Field k="Pumpkin palette" v={paletteSummary(b.palette)} wide />}
                {b.customer_notes && <Field k="Customer notes" v={b.customer_notes} wide />}
                {b.notes && <Field k="Private notes" v={b.notes} wide />}
                {b.gift_message && <Field k="Gift message" v={`“${b.gift_message}”`} wide />}
                {b.quoted_price != null && <Field k="Quote" v={`$${Number(b.quoted_price).toFixed(2)}${b.discount_type ? ` · ${b.discount_type === "amount" ? `$${b.discount_value}` : `${b.discount_value}%`} off` : ""}`} />}
              </div>
              {b.photo_base64 && <img src={b.photo_base64} alt="Porch" className="mt-3 max-h-40 border border-neutral-200" />}
              <div className="mt-3 text-xs text-neutral-500">Crew notes: ______________________________________________________________</div>
            </li>
          ))}
        </ol>
      </main>
    </div>
  );
}

const Field = ({ k, v, wide }) => (
  <div className={wide ? "col-span-2" : ""}>
    <span className="text-[10px] uppercase tracking-widest text-neutral-500 mr-2">{k}</span>
    <span>{v}</span>
  </div>
);
